import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Deck } from "@/types/linguadeck";
import { fetchGlobalDeckForStudy, createDeck, fetchDecksLight, DeckAlreadyImportedError } from "@/lib/db";
import { inspectQueryError, validateWriteReadConsistency, shouldValidateReadAfterWrite } from "@/diagnostics";
import { triggerBackgroundTtsForCards } from "@/lib/triggerBackgroundTts";
import { getLocalizedTestData } from "@/lib/testData";
import ExplainModal from "@/components/ExplainModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import TestStudyView from "@/components/TestStudyView";
import MatchMode from "@/components/MatchMode";
import PhilologyMode from "@/components/PhilologyMode";
import { useAuth } from "@/contexts/AuthContext";

type Mode = "learn" | "test" | "match" | "philology";

const centerStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "hsl(var(--bg))",
  fontFamily: "DM Sans, sans-serif",
  textAlign: "center",
  padding: 24,
};

export default function GlobalLibraryStudyPage() {
  const { t, i18n } = useTranslation();
  const { mode: urlMode, deckId } = useParams<{ mode: string; deckId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [addToAccountLoading, setAddToAccountLoading] = useState(false);
  const [alreadyInLibraryMsg, setAlreadyInLibraryMsg] = useState<string | null>(null);

  const mode: Mode = urlMode === "test" ? "test" : urlMode === "match" ? "match" : urlMode === "philology" ? "philology" : "learn";

  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [finished, setFinished] = useState(false);
  const [showExplain, setShowExplain] = useState(false);

  useEffect(() => {
    if (!deckId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    fetchGlobalDeckForStudy(deckId)
      .then((d) => {
        if (!d) setNotFound(true);
        else setDeck(d);
      })
      .catch((err) => {
        inspectQueryError({
          queryName: "fetchGlobalDeckForStudy",
          flow: "globalLibrary.studyDeck",
          view: "deck_summaries",
          filters: { deckId },
          responseError: err,
        });
        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [deckId]);

  const handleAddToAccount = async () => {
    if (!user) {
      navigate("/auth", { state: { returnTo: `/global-library/study/${mode}/${deckId}` } });
      return;
    }
    if (!deck) return;
    setAddToAccountLoading(true);
    setAlreadyInLibraryMsg(null);
    try {
      const created = await createDeck(
        user.id,
        {
          title: deck.title,
          lang: deck.lang,
          emoji: deck.emoji,
          level: deck.level,
          deckType: deck.deckType,
          studyModes: deck.studyModes ?? { cards: true, learn: true, test: true, match: true, philology: true },
          cards: deck.cards,
        },
        { sourceDeckId: deck.id }
      );
      if (created.cards?.length) {
        triggerBackgroundTtsForCards(created.cards, created.lang ?? "en");
      }
      if (shouldValidateReadAfterWrite("library.addDeckToLibrary")) {
        validateWriteReadConsistency({
          flow: "library.addDeckToLibrary",
          entity: "deck",
          entityId: created.id,
          userId: user.id,
          writeSucceeded: true,
          expectedPresence: true,
          fetchValidator: () => fetchDecksLight(user.id),
          checkPresence: (list) => Array.isArray(list) && list.some((d) => d.id === created.id),
        }).catch(() => {});
      }
      navigate("/library", { replace: true });
    } catch (err) {
      if (err instanceof DeckAlreadyImportedError) {
        setAlreadyInLibraryMsg(t("shared.deckAlreadyInLibrary"));
      } else {
        inspectQueryError({
          queryName: "createDeck",
          flow: "globalLibrary.addToAccount",
          table: "decks",
          responseError: err,
        });
        console.error("Failed to add deck:", err);
      }
      setAddToAccountLoading(false);
    }
  };

  const backHref = "/global-library";

  if (loading) {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎴</div>
        <div style={{ color: "hsl(var(--muted-foreground))", fontFamily: "DM Sans, sans-serif" }}>
          {t("shared.loadingDeck")}
        </div>
      </div>
    );
  }

  if (notFound || !deck) {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{t("shared.deckNotFound")}</div>
          <Link
          to={backHref}
          className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
        >
          {t("globalLibrary.backToCatalog")}
        </Link>
      </div>
    );
  }

  const lang = (i18n.language || "en").split("-")[0];
  const cardsWithTestData = deck.cards.filter((c) => getLocalizedTestData(c, lang));

  if (mode === "test") {
    if (cardsWithTestData.length === 0) {
      return (
        <div style={{ ...centerStyle, flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: 48 }}>📋</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{t("testMode.noTestCards")}</div>
          <Link
            to={backHref}
            className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
          >
            {t("globalLibrary.backToCatalog")}
          </Link>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-screen min-h-0" style={{ background: "hsl(var(--bg))", fontFamily: "DM Sans, sans-serif" }}>
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid hsl(var(--border))",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "hsl(var(--bg2))",
          }}
        >
          <Link
            to={backHref}
            className="inline-flex items-center py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
          >
            {t("globalLibrary.backToCatalog")}
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <LanguageSwitcher compact />
            <ThemeToggle compact />
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col" style={{ padding: 24 }}>
          <TestStudyView deck={deck} />
        </div>
      </div>
    );
  }

  if (mode === "philology") {
    if (!deck.cards || deck.cards.length === 0) {
      return (
        <div style={{ ...centerStyle, flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: 48 }}>🌳</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{t("philology.empty")}</div>
          <Link
            to={backHref}
            className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
          >
            {t("globalLibrary.backToCatalog")}
          </Link>
        </div>
      );
    }
    return (
      <div className="flex flex-col h-screen min-h-0" style={{ background: "hsl(var(--bg))", fontFamily: "DM Sans, sans-serif" }}>
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid hsl(var(--border))",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "hsl(var(--bg2))",
          }}
        >
          <Link
            to={backHref}
            className="inline-flex items-center py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
          >
            {t("globalLibrary.backToCatalog")}
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <LanguageSwitcher compact />
            <ThemeToggle compact />
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col" style={{ padding: 24 }}>
          <PhilologyMode deck={deck} cardIndex={cardIndex} onPrevCard={() => { if (cardIndex > 0) setCardIndex((i) => i - 1); }} onNextCard={() => { if (cardIndex + 1 < deck.cards.length) setCardIndex((i) => i + 1); }} />
        </div>
      </div>
    );
  }

  if (mode === "match") {
    const gameCards = deck.cards?.filter((c) => (c.front || "").trim() && (c.back || "").trim()) ?? [];
    if (gameCards.length < 2) {
      return (
        <div style={{ ...centerStyle, flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: 48 }}>🎮</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{t("games.needCards")}</div>
          <Link
            to={backHref}
            className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
          >
            {t("globalLibrary.backToCatalog")}
          </Link>
        </div>
      );
    }
    return (
      <div className="flex flex-col h-screen min-h-0" style={{ background: "hsl(var(--bg))", fontFamily: "DM Sans, sans-serif" }}>
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid hsl(var(--border))",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "hsl(var(--bg2))",
          }}
        >
          <Link
            to={backHref}
            className="inline-flex items-center py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
          >
            {t("globalLibrary.backToCatalog")}
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <LanguageSwitcher compact />
            <ThemeToggle compact />
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col items-center overflow-auto">
          <MatchMode
            cards={deck.cards ?? []}
            onBack={() => navigate(backHref)}
          />
        </div>
      </div>
    );
  }

  const cards = deck.cards;
  const currentCard = cards[cardIndex];

  const handleNext = (correct: boolean) => {
    setScore((s) => ({ correct: s.correct + (correct ? 1 : 0), incorrect: s.incorrect + (correct ? 0 : 1) }));
    if (cardIndex + 1 >= cards.length) setFinished(true);
    else {
      setCardIndex((i) => i + 1);
      setFlipped(false);
    }
  };

  const handleRestart = () => {
    setCardIndex(0);
    setFlipped(false);
    setScore({ correct: 0, incorrect: 0 });
    setFinished(false);
  };

  return (
    <div className="min-h-screen flex flex-col overflow-y-auto pb-16" style={{ background: "hsl(var(--bg))", fontFamily: "DM Sans, sans-serif" }}>
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid hsl(var(--border))",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "hsl(var(--bg2))",
        }}
      >
        <Link
          to={backHref}
          className="inline-flex items-center py-2.5 px-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-medium no-underline hover:bg-[hsl(var(--bg3))]/80 transition-colors"
        >
          {t("globalLibrary.backToCatalog")}
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {alreadyInLibraryMsg && (
            <span style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>{alreadyInLibraryMsg}</span>
          )}
          <LanguageSwitcher compact />
          <ThemeToggle compact />
          <button
            onClick={handleAddToAccount}
            disabled={addToAccountLoading}
            style={{
              padding: "8px 16px",
              borderRadius: 50,
              border: "none",
              background: "hsl(var(--primary))",
              color: "white",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 13,
              fontWeight: 500,
              cursor: addToAccountLoading ? "wait" : "pointer",
              opacity: addToAccountLoading ? 0.8 : 1,
            }}
          >
            {addToAccountLoading ? "…" : t("globalLibrary.addToLibrary")}
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center" style={{ padding: "32px 16px 48px" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>{deck.emoji}</div>
          <h1 style={{ fontFamily: "Instrument Serif, serif", fontSize: 28, margin: 0, color: "hsl(var(--foreground))" }}>{deck.title}</h1>
          <div style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", marginTop: 6 }}>
            {deck.lang.toUpperCase()}{deck.level ? ` · ${deck.level}` : ""} · {t("sidebar.cardsCount", { count: cards.length })}
          </div>
        </div>

        {finished ? (
          <div
            style={{
              background: "hsl(var(--card-bg))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "var(--radius)",
              padding: 40,
              textAlign: "center",
              maxWidth: 420,
              width: "100%",
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{t("shared.sessionComplete")}</div>
            <div style={{ fontSize: 14, color: "hsl(var(--muted-foreground))", marginBottom: 24 }}>
              {t("shared.cardsReviewed", { count: cards.length })}
            </div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 28 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "hsl(var(--accent2))" }}>{score.correct}</div>
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{t("shared.correct")}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "hsl(var(--accent3))" }}>{score.incorrect}</div>
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{t("shared.incorrect")}</div>
              </div>
            </div>
            <button
              onClick={handleRestart}
              style={{
                padding: "10px 28px",
                borderRadius: 50,
                border: "none",
                background: "hsl(var(--primary))",
                color: "white",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              🔄 {t("shared.playAgain")}
            </button>
          </div>
        ) : (
          <div style={{ width: "100%", maxWidth: 720 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
              <div style={{ flex: 1, height: 4, background: "hsl(var(--bg3))", borderRadius: 2, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${(cardIndex / cards.length) * 100}%`,
                    background: "hsl(var(--primary))",
                    borderRadius: 2,
                    transition: "width .3s",
                  }}
                />
              </div>
              <span style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>
                {cardIndex + 1} / {cards.length}
              </span>
            </div>

            <div className="flex flex-row items-center justify-center gap-5 md:gap-10" style={{ width: "100%" }}>
              <button
                type="button"
                onClick={() => { if (cardIndex > 0) { setCardIndex((i) => i - 1); setFlipped(false); } }}
                disabled={cardIndex === 0}
                className="w-14 h-14 rounded-full border-2 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-[hsl(var(--bg3))] text-blue-500 border-blue-200 dark:border-blue-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                aria-label={t("shared.previousCard")}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div
                onClick={() => setFlipped((f) => !f)}
                className="flex-1 min-w-0"
                style={{
                  background: "hsl(var(--card-bg))",
                  border: "2px solid hsl(var(--border))",
                  borderRadius: "var(--radius)",
                  padding: "56px 44px",
                  textAlign: "center",
                  cursor: "pointer",
                  minHeight: 320,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 20,
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 4px 24px rgba(0,0,0,.06)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: flipped
                      ? "linear-gradient(90deg, hsl(var(--accent2)), hsl(var(--primary)))"
                      : "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
                  }}
                />
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.12em" }}>
                  {flipped ? t("shared.answer") : t("shared.question")}
                </div>
                <div
                  style={{
                    fontSize: (flipped ? currentCard.back : currentCard.front).length > 60 ? 20 : 26,
                    fontWeight: 600,
                    color: flipped ? "hsl(var(--accent2))" : "hsl(var(--foreground))",
                    lineHeight: 1.45,
                  }}
                >
                  {flipped ? currentCard.back : currentCard.front}
                </div>
                {!flipped && currentCard.hint && (
                  <div style={{ fontSize: 14, color: "hsl(var(--muted-foreground))" }}>
                    {t("shared.hint")}: {currentCard.hint}
                  </div>
                )}
                {flipped && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowExplain(true); }}
                    style={{
                      marginTop: 16,
                      padding: "8px 16px",
                      borderRadius: 50,
                      border: "none",
                      background: "hsl(var(--bg3))",
                      color: "hsl(var(--foreground))",
                      fontFamily: "DM Sans, sans-serif",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    ✨ {t("shared.explain")}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => { if (cardIndex < cards.length - 1) { setCardIndex((i) => i + 1); setFlipped(false); } }}
                disabled={cardIndex === cards.length - 1}
                className="w-14 h-14 rounded-full border-2 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-[hsl(var(--bg3))] text-blue-500 border-blue-200 dark:border-blue-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                aria-label={t("shared.nextCard")}
              >
                <ArrowRight className="w-6 h-6" />
              </button>
            </div>

            {flipped && (
              <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                <button
                  onClick={() => handleNext(false)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    borderRadius: 50,
                    border: "1px solid rgba(255,90,138,.4)",
                    background: "rgba(255,90,138,.08)",
                    color: "hsl(var(--accent3))",
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  ✗ {t("shared.incorrect")}
                </button>
                <button
                  onClick={() => handleNext(true)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    borderRadius: 50,
                    border: "1px solid rgba(0,229,160,.4)",
                    background: "rgba(0,229,160,.08)",
                    color: "hsl(var(--accent2))",
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  ✓ {t("shared.correct")}
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 20 }}>
              <span style={{ fontSize: 13, color: "hsl(var(--accent2))" }}>✓ {score.correct}</span>
              <span style={{ fontSize: 13, color: "hsl(var(--accent3))" }}>✗ {score.incorrect}</span>
            </div>
          </div>
        )}
      </div>

      {showExplain && currentCard && deck && (
        <ExplainModal card={currentCard} deck={deck} uiLang={lang} onClose={() => setShowExplain(false)} />
      )}
    </div>
  );
}
